import React, { useEffect } from "react";
import { useMap } from "react-leaflet";
import L, { LatLngTuple } from "leaflet";
import "./FieldLayer.css";

// --- Type Definitions ---
interface FieldProperties {
  field_id: number;
  leasee_name: string;
  parcel_ids: string;
  tillable_acres: number;
  rent_per_acre: number;
  productivity_rating: number;
  renewal_date: string;
}

interface FieldFeature {
  type: "Feature";
  properties: FieldProperties;
  geometry: GeoJSON.Geometry;
}

interface FieldLayerProps {
  fields: FieldFeature[];
  highlightFieldId: number | null;
  onNotesClick: (fieldId: number) => void;
  showAcres?: boolean;
}

const FieldLayer: React.FC<FieldLayerProps> = ({
  fields,
  highlightFieldId,
  onNotesClick,
  showAcres = true,
}) => {
  const map = useMap();

  useEffect(() => {
    if (!fields.length) return;

    const alreadyLabeled = new Set<number>();

    const geoJsonData: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: fields,
    };

    // --- Draw Directional Dashes Like Arrows
    const drawDirectionalLine = (coords: number[][]) => {
      for (let i = 0; i < coords.length - 1; i++) {
        const [lng1, lat1] = coords[i];
        const [lng2, lat2] = coords[i + 1];
        const line = L.polyline(
          [
            [lat1, lng1],
            [lat2, lng2],
          ],
          {
            color: "gray",
            weight: 1,
            dashArray: "6, 6, 1, 6",
            dashOffset: "0",
            interactive: false,
          }
        );
        line.addTo(map);
      }
    };

    const layer = L.geoJSON(geoJsonData, {
      style: (feature) => ({
        color: feature?.properties?.field_id === highlightFieldId ? "yellow" : "yellow",
        weight: .75,
        fillColor: "transparent",
        fillOpacity: 0,
      }),
      onEachFeature: (feature, layer) => {
        if (!feature?.properties) return;
        const props = feature.properties;

        layer.on("click", () => {
          layer.openPopup();
        });

        const container = document.createElement("div");
        container.className = "parcel-popup-details";
        container.innerHTML = `
          <h3>Field Details</h3>
          <table class="details-table">
            <tr><th>Field ID:</th><td>${props.field_id}</td></tr>
            <tr><th>Farmer Name:</th><td>${props.leasee_name}</td></tr>
            <tr><th>Total Tillable Acres:</th><td>${props.tillable_acres}</td></tr>
            <tr><th>Rent per Acre:</th><td>$${props.rent_per_acre}</td></tr>
            <tr><th>Productivity Rating:</th><td>${props.productivity_rating}</td></tr>
            <tr><th>Renewal Date:</th><td>${props.renewal_date}</td></tr>
          </table>
          <div class="button-box">
            <button class="notes-button">Add Note</button>
            <button class="close-button">Close</button>
          </div>
        `;

        container.querySelector(".notes-button")?.addEventListener("click", () => {
          onNotesClick(props.field_id);
        });

        container.querySelector(".close-button")?.addEventListener("click", () => {
          layer.closePopup();
        });

        layer.bindPopup(container);

        // Acres Label Logic
        if (showAcres && !alreadyLabeled.has(props.field_id)) {
          alreadyLabeled.add(props.field_id);
          const acres = Math.round(Number(props.tillable_acres) || 0);
          if (acres > 0) {
            const labelText = `${acres}AC`;

            const getCenter = (): L.LatLng | null => {
              if (feature.geometry.type === "Polygon") {
                const latlngs: LatLngTuple[] = (feature.geometry.coordinates[0] as number[][]).map(
                  ([lng, lat]) => [lat, lng]
                );
                return L.polygon(latlngs).getBounds().getCenter();
              } else if (feature.geometry.type === "MultiPolygon") {
                const allLatLngs: LatLngTuple[] = [];
                (feature.geometry.coordinates as number[][][][]).forEach((polygon) => {
                  polygon[0].forEach(([lng, lat]) => {
                    allLatLngs.push([lat, lng]);
                  });
                });
                return L.polygon(allLatLngs).getBounds().getCenter();
              }
              return null;
            };

            const center = getCenter();
            if (center) {
              const label = L.marker(center, {
                icon: L.divIcon({
                  className: "field-label",
                  html: `<div>${labelText}</div>`,
                }),
                interactive: false,
              });
              label.setZIndexOffset(1000).addTo(map);
            }
          }
        }

        // Directional Arrows Along Boundary
        if (feature.geometry.type === "Polygon") {
          drawDirectionalLine(feature.geometry.coordinates[0] as number[][]);
        } else if (feature.geometry.type === "MultiPolygon") {
          (feature.geometry.coordinates as number[][][][]).forEach((polygon) => {
            drawDirectionalLine(polygon[0]);
          });
        }
      },
    });

    // Zoom to highlighted field
    if (highlightFieldId) {
      const highlighted = fields.find(f => f.properties.field_id === highlightFieldId);
      if (highlighted?.geometry?.type === "Polygon") {
        const bounds = L.latLngBounds([]);
        (highlighted.geometry.coordinates[0] as number[][]).forEach(([lng, lat]) =>
          bounds.extend([lat, lng])
        );
        if (bounds.isValid()) map.fitBounds(bounds, { maxZoom: 16 });
      } else if (highlighted?.geometry?.type === "MultiPolygon") {
        const bounds = L.latLngBounds([]);
        (highlighted.geometry.coordinates as number[][][][]).forEach(polygon => {
          polygon[0].forEach(([lng, lat]) => bounds.extend([lat, lng]));
        });
        if (bounds.isValid()) map.fitBounds(bounds, { maxZoom: 16 });
      }
    }

    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [fields, highlightFieldId, map, onNotesClick, showAcres]);

  return null;
};

export default FieldLayer;
